import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Badge,
  Spinner,
} from 'reactstrap'
import {
  FaCheckCircle,
  FaSearch,
  FaArrowRight,
  FaTruck,
  FaUser,
  FaExclamationTriangle,
  FaTimes,
  FaCheck
} from 'react-icons/fa'
import { Formik, Field } from 'formik'
import * as Yup from 'yup'
import { nominateTruck } from '../../store/slices/transportRequestSlice'
import { searchDrivers } from '../../store/slices/driverSlice'
import api from '../../services/api'
import { GLOBAL_SEARCH_TRUCK } from '../../helpers/url_helper'

const NominateTruck = ({ isOpen, toggle, requestId, proposalId, requestData }) => {
  const dispatch = useDispatch()
  const { drivers = [], loading: driversLoading } = useSelector(state => state.driver)

  // Truck search state
  const [truckSearchQuery, setTruckSearchQuery] = useState('')
  const [truckSearching, setTruckSearching] = useState(false)
  const [truckSearchResult, setTruckSearchResult] = useState(null)
  const [truckSearchError, setTruckSearchError] = useState('')
  const [selectedTruck, setSelectedTruck] = useState(null)

  // Driver dropdown / filter state
  const [selectedDriver, setSelectedDriver] = useState(null)
  const [driverNameQuery, setDriverNameQuery] = useState('')
  const [driverLicenseQuery, setDriverLicenseQuery] = useState('')
  const [driverIsActive, setDriverIsActive] = useState('')
  const debounceRef = useRef(null)

  useEffect(() => {
    if (isOpen) {
      // Fetch initial drivers using search-driver endpoint
      dispatch(searchDrivers({ page: 1, limit: 100 }))
    }
  }, [dispatch, isOpen])

  const handleSearchTruck = async () => {
    if (!truckSearchQuery.trim()) return
    setTruckSearching(true)
    setTruckSearchResult(null)
    setTruckSearchError('')
    setSelectedTruck(null)
    try {
      const response = await api.get(`${GLOBAL_SEARCH_TRUCK}/${truckSearchQuery.trim().toUpperCase()}`)
      const truck = response.data?.data || response.data?.truck || response.data
      if (truck && (truck.truck_id || truck.id)) {
        setTruckSearchResult(truck)
      } else {
        setTruckSearchError('No truck found with this number.')
      }
    } catch (error) {
      setTruckSearchError(error.response?.data?.error || 'Truck not found.')
    } finally {
      setTruckSearching(false)
    }
  }

  const handleReset = () => {
    setTruckSearchQuery('')
    setTruckSearchResult(null)
    setTruckSearchError('')
    setSelectedTruck(null)
    setSelectedDriver(null)
  }

  const handleDriverSelect = (driverId) => {
    const driver = drivers.find(d => String(d.driver_id || d.id || d._id) === String(driverId))
    setSelectedDriver(driver)
  }

  const handleDriverSearch = async () => {
    const params = {}
    if (driverNameQuery && driverNameQuery.trim()) params.name = driverNameQuery.trim()
    if (driverLicenseQuery && driverLicenseQuery.trim()) params.licenseNumber = driverLicenseQuery.trim()
    if (driverIsActive !== '') params.isActive = driverIsActive === 'true' || driverIsActive === true
    params.page = 1
    params.limit = 100
    await dispatch(searchDrivers(params))
  }

  const handleDriverReset = () => {
    setDriverNameQuery('')
    setDriverLicenseQuery('')
    setDriverIsActive('')
    dispatch(searchDrivers({ page: 1, limit: 100 }))
  }

  // Debounced live search: when user types filters, wait 500ms then search
  useEffect(() => {
    if (!isOpen) return
    const shouldSearch = (driverNameQuery && driverNameQuery.trim()) || (driverLicenseQuery && driverLicenseQuery.trim()) || driverIsActive !== ''
    if (!shouldSearch) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      handleDriverSearch()
    }, 500)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [driverNameQuery, driverLicenseQuery, driverIsActive, isOpen])

  const validationSchema = Yup.object().shape({
    truck_id: Yup.string()
      .required('Please search and select a truck')
      .test('not-empty', 'Please search and select a truck', value => !!value),
    driver_id: Yup.string()
      .required('Please select a driver')
      .test('not-empty', 'Please select a driver', value => value !== '' && value !== undefined && value !== null),
  })

  const initialValues = { truck_id: '', driver_id: '' }

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const truckData = {
        truck_id: parseInt(values.truck_id),
        driver_id: parseInt(values.driver_id),
      }
      const result = await dispatch(nominateTruck({ requestId, proposalId, truckData })).unwrap()
      if (result) {
        resetForm()
        handleReset()
        toggle()
      }
    } catch (error) {
      console.error('Nomination error:', error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="aurora-modal">
      <ModalHeader toggle={toggle} className="border-0">
        <span className="neo-text">Resource Nomination Protocol</span>
      </ModalHeader>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, handleSubmit, isSubmitting, setFieldValue, errors, touched }) => (
          <form onSubmit={handleSubmit}>
            <ModalBody className="p-4">
              <div className="alert-glass-success mb-4 p-3 rounded-3 d-flex align-items-center">
                <FaCheckCircle className="me-3 text-success" size={24} />
                <div>
                  <h6 className="mb-0 fw-bold">Negotiation Accepted</h6>
                  <small>Proposal accepted. You can now nominate vehicle and driver.</small>
                </div>
              </div>

              {requestData && (
                <div className="proposal-summary mb-4 p-3 glass-effect rounded-3 border-glass">
                  <h6 className="neo-text mb-3">Request Summary</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Protocol ID</span>
                        <span className="value fw-bold">{requestData.request_number}</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Vector</span>
                        <span className="value">{requestData.origin} <FaArrowRight size={10} className="mx-1" /> {requestData.destination}</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Schedule</span>
                        <span className="value">{new Date(requestData.loading_date).toLocaleDateString()}</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Payload</span>
                        <span className="value">{requestData.quantity} {requestData.quantity_unit}</span>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              {/* ── Truck Search ── */}
              <FormGroup className="mb-4">
                <Label className="detail-label">Vehicle Registration Number</Label>
                <div className="d-flex gap-2">
                  <Input
                    className="glass-input"
                    placeholder="e.g. MH12AB1234"
                    value={truckSearchQuery}
                    onChange={e => setTruckSearchQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleSearchTruck())}
                    disabled={!!selectedTruck}
                  />
                  {!selectedTruck ? (
                    <Button className="btn-gradient px-4" type="button" onClick={handleSearchTruck} disabled={truckSearching || !truckSearchQuery.trim()}>
                      {truckSearching ? <Spinner size="sm" /> : <><FaSearch className="me-2" />Search</>}
                    </Button>
                  ) : (
                    <Button color="secondary" className="glass-effect px-4" type="button" onClick={() => {
                      setSelectedTruck(null)
                      setTruckSearchResult(null)
                      setTruckSearchQuery('')
                      setFieldValue('truck_id', '')
                    }}>
                      <FaTimes className="me-2" />Clear
                    </Button>
                  )}
                </div>
                {truckSearchError && <div className="text-danger small mt-2 fw-bold">{truckSearchError}</div>}
              </FormGroup>

              {/* Truck search result — click to select */}
              {truckSearchResult && !selectedTruck && (
                <div
                  className="p-3 glass-effect border-glass rounded mb-4 cursor-pointer hover-bright"
                  onClick={() => {
                    const truckId = truckSearchResult.truck_id || truckSearchResult.id
                    setSelectedTruck(truckSearchResult)
                    setTruckSearchResult(null)
                    setFieldValue('truck_id', String(truckId))
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0 neo-text text-primary">
                      <FaTruck className="me-2" />
                      {truckSearchResult.truckNumber || truckSearchResult.truck_number}
                    </h6>
                    <Badge color="primary" className="status-pill">Select Vehicle</Badge>
                  </div>
                  <Row className="g-3">
                    <Col md={4} xs={6}><small className="text-muted d-block">Type</small><span className="text-main fw-bold">{truckSearchResult.truckType || truckSearchResult.truck_type || '—'}</span></Col>
                    <Col md={4} xs={6}><small className="text-muted d-block">Capacity</small><span className="text-main fw-bold">{truckSearchResult.capacity || '—'} {truckSearchResult.capacityUnit || truckSearchResult.capacity_unit || ''}</span></Col>
                    <Col md={4} xs={6}><small className="text-muted d-block">Owner</small><span className="text-main fw-bold">{truckSearchResult.ownerName || truckSearchResult.owner_name || '—'}</span></Col>
                  </Row>
                </div>
              )}

              {/* Selected truck card */}
              {selectedTruck && (
                <div className="p-3 glass-effect border-success rounded-3 mb-4">
                  <h6 className="mb-3 text-success d-flex align-items-center">
                    <FaCheckCircle className="me-2" />
                    Vehicle Selected
                  </h6>
                  <Row className="g-3">
                    <Col md={4} xs={6}><small className="text-muted d-block">Vehicle No</small><span className="text-main fw-bold">{selectedTruck.truckNumber || selectedTruck.truck_number}</span></Col>
                    <Col md={4} xs={6}><small className="text-muted d-block">Type</small><span className="text-main fw-bold">{selectedTruck.truckType || selectedTruck.truck_type || '—'}</span></Col>
                    <Col md={4} xs={6}><small className="text-muted d-block">Capacity</small><span className="text-main fw-bold">{selectedTruck.capacity || '—'} {selectedTruck.capacityUnit || selectedTruck.capacity_unit || ''}</span></Col>
                  </Row>
                </div>
              )}

              {/* ── Driver Search Filters ── */}
              <FormGroup className="mb-3">
                <Label className="detail-label">Filter Drivers</Label>
                <div className="d-flex gap-2 mb-2">
                  <Input
                    placeholder="Search by name"
                    value={driverNameQuery}
                    onChange={e => setDriverNameQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleDriverSearch())}
                    className="glass-input"
                  />
                  <Input
                    placeholder="Search by license"
                    value={driverLicenseQuery}
                    onChange={e => setDriverLicenseQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleDriverSearch())}
                    className="glass-input"
                  />
                  <Input type="select" value={driverIsActive} onChange={e => setDriverIsActive(e.target.value)} className="glass-input">
                    <option value="">Any</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Input>
                </div>
                <div className="d-flex gap-2">
                  <Button color="primary" className="px-3" type="button" onClick={handleDriverSearch} disabled={driversLoading}>
                    {driversLoading ? <Spinner size="sm" /> : <><FaSearch className="me-2" />Search</>}
                  </Button>
                  <Button color="secondary" className="px-3" type="button" onClick={handleDriverReset}>
                    Reset
                  </Button>
                </div>
              </FormGroup>

              {/* ── Driver Dropdown ── */}
              <FormGroup className="mb-4">
                <Label className="detail-label">Select Driver</Label>
                <Field name="driver_id">
                  {({ field, form }) => (
                    <Input
                      type="select"
                      {...field}
                      className="glass-input"
                      onChange={e => {
                        const selectedId = e.target.value
                        form.setFieldValue('driver_id', selectedId)
                        form.setFieldTouched('driver_id', true)
                        handleDriverSelect(selectedId)
                      }}
                      invalid={form.touched.driver_id && !!form.errors.driver_id}
                    >
                      <option value="">-- Select Driver --</option>
                      {drivers.map(driver => {
                        const driverId = driver.driver_id || driver.id || driver._id
                        return (
                          <option key={driverId} value={driverId}>
                            {driver.name} | {driver.drivingLicenseNumber || driver.license_number}
                          </option>
                        )
                      })}
                    </Input>
                  )}
                </Field>
                {touched.driver_id && errors.driver_id && (
                  <div className="text-danger small mt-2 fw-bold">{errors.driver_id}</div>
                )}
              </FormGroup>

              {/* Selected driver details */}
              {selectedDriver && (
                <div className="p-3 glass-effect border-glass rounded-3 mb-4">
                  <h6 className="mb-3 neo-text d-flex align-items-center">
                    <FaUser className="me-2" />
                    Driver Details
                  </h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Agent Name</span>
                        <span className="value text-main">{selectedDriver.name}</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Authorization ID</span>
                        <span className="value text-main">{selectedDriver.drivingLicenseNumber || selectedDriver.license_number}</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Comms Link</span>
                        <span className="value text-main">{selectedDriver.phone}</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Authorization Expiry</span>
                        <span className="value text-main">
                          {selectedDriver.licenseValidUpto ? new Date(selectedDriver.licenseValidUpto).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              <div className="alert-glass-warning p-3 rounded-3">
                <FaExclamationTriangle className="me-2 text-warning" />
                <strong>Note:</strong> Selection is subject to verification by the contractor.
              </div>
            </ModalBody>

            <ModalFooter className="border-0">
              <Button color="secondary" className="glass-effect" onClick={toggle} disabled={isSubmitting} type="button">
                Abort
              </Button>
              <Button color="success" className="btn-gradient" type="submit" disabled={isSubmitting || !values.truck_id || !values.driver_id}>
                {isSubmitting ? <><Spinner size="sm" className="me-2" />Submitting...</> : <><FaCheck className="me-2" />Confirm Nomination</>}
              </Button>
            </ModalFooter>
          </form>
        )}
      </Formik>
    </Modal>
  )
}

export default NominateTruck
